import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from "../components/ui/button";
import LanguageSwitcher from './LanguageSwitcher';
import { Link, useNavigate } from 'react-router-dom'; // استيراد Link و useNavigate
import { useDispatch, useSelector } from 'react-redux';
import { fetchLogo } from '../rtk/slices/logo-slice';
import { fetchVisitor } from '../rtk/slices/visitor-slice';

const Navigation = ({ activeSection, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const visitor = useSelector((state) => state.visitor.visitor);
  const navigate = useNavigate(); // استخدام useNavigate للتحكم في التوجيه إذا لزم الأمر
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const dispatch = useDispatch();
  const { logo } = useSelector((state) => state.logo);

  useEffect(() => {
    dispatch(fetchLogo());
    dispatch(fetchVisitor());
  }, [dispatch]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng');
    if (savedLanguage && i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const isRTL = i18n.language === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (visitor.length > 0 && !sessionStorage.getItem('visit')) {
      fetch(`${process.env.REACT_APP_PROJECTS_API}/components/s_d.php?visitor=${visitor['0'].total}`, { method: 'POST' });
      sessionStorage.setItem('visit', 1);
    }
  }, [visitor]);

  const navItems = [
    { key: 'hero', label: t('nav.home'), type: 'section' },
    { key: 'catalog', label: t('nav.catalog'), type: 'section' },
    { key: 'products', label: t('nav.products'), type: 'route', path: '/products' }, // تحديد النوع والمسار
    { key: 'about', label: t('nav.about'), type: 'section' },
    { key: 'reviews', label: t('nav.reviews'), type: 'section' },
    { key: 'contact', label: t('nav.contact'), type: 'section' }
  ];

  const handleNavItemClick = (item) => {
    if (item.type === 'section') {
      onNavigate(item.key);
    } else if (item.type === 'route' && item.path) {
      navigate(item.path);
    }
    setIsOpen(false); // لإغلاق القائمة المنسدلة في الجوال عند الاختيار
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full max-w-full
        bg-gradient-to-r from-[rgba(90,56,30,0.85)] to-[rgba(53,37,19,0.80)]
        backdrop-blur-sm`} dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="w-full max-w-full px-3 sm:px-4">
        <div className={`flex items-center justify-between h-20 sm:h-20 w-full ${isRTL ? 'direction-reverse' : ''}`}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} flex-shrink-0`}
          >
            <button onClick={() => handleNavItemClick({ type: 'section', key: 'hero' })} >
              <img id='logo'
                src={logo.length > 0 && `${process.env.REACT_APP_PROJECTS_API}assest/imgs/${logo[0].logo}`}
                style={{ width: '5.5rem', borderRadius: '.5rem' }} alt="Logo" />
            </button>
          </motion.div>

          <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-6 lg:space-x-8'} flex-1 justify-center max-w-full overflow-hidden`}>
            {navItems.map((item) => {
              const itemClasses = `font-medium transition-all duration-300 hover:scale-105 px-1 whitespace-nowrap text-sm lg:text-lg ${
                // تحديد activeSection بشكل مختلف للمسارات
                (item.type === 'section' && activeSection === item.key) || (item.type === 'route' && window.location.pathname === item.path)
                  ? isScrolled
                    ? 'text-amber-200 border-b-2 border-amber-200'
                    : 'text-amber-200 border-b-2 border-amber-200'
                  : isScrolled
                    ? 'text-white hover:text-amber-200'
                    : 'text-white hover:text-amber-200'
                }`;

              if (item.type === 'route' && item.path) {
                return (
                  <Link key={item.key} to={item.path} className={itemClasses}>
                    {item.label}
                  </Link>
                );
              }
              return (
                <button
                  key={item.key}
                  onClick={() => handleNavItemClick(item)}
                  className={itemClasses}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2 sm:space-x-4'} flex-shrink-0`}>
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="sm"
              className={`md:hidden p-2 ${isScrolled ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/20'}`}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="h-4 w-4 sm:h-5 sm:w-5" /> : <Menu className="h-4 w-4 sm:h-5 sm:w-5" />}
            </Button>
          </div>
        </div>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-white/20 bg-white/95 backdrop-blur-md w-full max-w-full"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <div className="py-3 sm:py-4 space-y-1 sm:space-y-2 w-full">
              {navItems.map((item) => {
                const itemClasses = `block w-full ${isRTL ? 'text-right' : 'text-left'} px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${(item.type === 'section' && activeSection === item.key) || (item.type === 'route' && window.location.pathname === item.path)
                  ? 'bg-amber-100 text-amber-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
                  }`;

                if (item.type === 'route' && item.path) {
                  return (
                    <Link key={item.key} to={item.path} className={itemClasses} onClick={() => setIsOpen(false)}>
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <button
                    key={item.key}
                    onClick={() => handleNavItemClick(item)}
                    className={itemClasses}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navigation;